import type React from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import bgImage from "../assets/images/bg-img2.png";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const isPasswordTooShort = password.length > 0 && password.length < 8;
  const doPasswordsMismatch =
    password.length > 0 &&
    confirmPassword.length > 0 &&
    password !== confirmPassword;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Navigate directly to SetNewPassword page without API call
    navigate("/set-new-password");
  };

  const handleResend = () => {
  };

  const handleContinue = () => {
    // Navigate directly to SetNewPassword page
    navigate("/set-new-password");
  };

  const handleOtpContinue = () => {
    // Navigate directly to SetNewPassword page
    navigate("/set-new-password");
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus to next input
    if (value && index < 3) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) return;
    if (password !== confirmPassword) return;
    console.log("Password reset successful");
    setShowSuccessScreen(true);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Section - Form */}
      <div className="w-full lg:w-1/2 flex flex-col mt-20 md:mt-32 px-8 md:px-16 lg:pr-[8rem] bg-white">
        <div className="max-w-md mx-auto w-full">
          {/* Back Button */}
          <button
            onClick={() => {
              if (showSuccessScreen) {
                setShowSuccessScreen(false);
                setShowPasswordReset(false);
                setPassword("");
                setConfirmPassword("");
              } else if (showPasswordReset) {
                setShowPasswordReset(false);
              } else if (showOtp) {
                setShowOtp(false);
              } else if (isSubmitted) {
                setIsSubmitted(false);
                setEmail("");
              } else {
                window.history.back();
              }
            }}
            className="flex items-center font-medium hover:underline text-[#000000] hover:text-black mb-8 transition-colors"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back
          </button>

          {!isSubmitted ? (
            <>
              {/* Form Screen */}
              <h1 className="text-4xl font-bold text-[#171923] mb-3">
                Forget Password
              </h1>
              <p className="text-[#171923] text-base mb-8 leading-relaxed">
                No worries! Enter your email address below, and we'll send you a
                link to reset your password.
              </p>

              <form onSubmit={handleSubmit} className="space-y-12">
                <div>
                  <label className="block text-base font-medium text-[#718096] mb-2">
                    E-mail
                  </label>
                  <input
                    type="email"
                    placeholder="example@gmail.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-[#F7FAFC] px-4 py-3 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-lime-400 placeholder-gray-400"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn-cta w-full py-3 font-medium rounded-md"
                >
                  Submit
                </button>
              </form>
            </>
          ) : !showOtp ? (
            <>
              {/* Confirmation Screen */}
              <h1 className="text-4xl font-bold text-[#171923] mb-4">
                Check your Email
              </h1>
              <p className="text-[#171923] text-base mb-8 leading-relaxed">
                We sent a password reset link to your email. Please check your
                inbox
              </p>

              <div className="space-y-4">
                <button
                  onClick={handleContinue}
                  className="btn-cta w-full py-3 font-medium rounded-md"
                >
                  Continue
                </button>

                <button
                  onClick={handleResend}
                  className="w-full text-center py-2 text-black font-medium hover:underline transition-colors"
                >
                  Didn't received the email? Resend
                </button>
              </div>
            </>
          ) : !showPasswordReset ? (
            <>
              <h1 className="text-4xl font-bold text-[#171923] mb-4">
                Enter your <br /> verification code
              </h1>
              <p className="text-[#171923] text-base mb-8 leading-relaxed">
                Enter the OTP sent to your email to verify your identity and
                continue securely.
              </p>

              {/* OTP Input Boxes */}
              <div className="flex gap-4 mb-6 justify-start">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    className="w-16 h-16 border-2 border-gray-300 rounded-md text-center text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-lime-400 focus:border-lime-400 transition-colors"
                    inputMode="numeric"
                  />
                ))}
              </div>

              {/* Timer */}
              <p className="text-[#171923] font-medium mb-8">(2:30)</p>

              {/* Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={handleOtpContinue}
                  className="btn-cta flex-1 py-3 font-medium rounded-md"
                >
                  Continue
                </button>
                <button
                  onClick={() => console.log("Resend OTP")}
                  className="flex-1 py-3 border-2 border-gray-300 text-[#171923] font-medium rounded-md hover:border-gray-400 transition-colors"
                >
                  Resend Again
                </button>
              </div>
            </>
          ) : !showSuccessScreen ? (
            <>
              <h1 className="text-4xl font-bold text-[#171923] mb-4">
                Set new password
              </h1>
              <p className="text-[#171923] text-base mb-8 leading-relaxed">
                Must be at least 8 characters.
              </p>

              <form onSubmit={handlePasswordSubmit} className="space-y-6">
                {/* Password Input */}
                <div>
                  <label className="block text-base font-medium text-[#718096] mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`w-full bg-[#F7FAFC] px-4 py-3 border ${isPasswordTooShort || doPasswordsMismatch ? "border-red-500 focus:ring-red-500" : "border-gray-200 focus:ring-lime-400"} rounded-md focus:outline-none focus:ring-2 placeholder-gray-400 pr-10`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {isPasswordTooShort && (
                    <p className="mt-2 text-sm text-red-600">
                      Password must be at least 8 characters.
                    </p>
                  )}
                </div>

                {/* Confirm Password Input */}
                <div>
                  <label className="block text-base font-medium text-[#718096] mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`w-full bg-[#F7FAFC] px-4 py-3 border ${doPasswordsMismatch ? "border-red-500 focus:ring-red-500" : "border-gray-200 focus:ring-lime-400"} rounded-md focus:outline-none focus:ring-2 placeholder-gray-400 pr-10`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={20} />
                      ) : (
                        <Eye size={20} />
                      )}
                    </button>
                  </div>
                  {doPasswordsMismatch && (
                    <p className="mt-2 text-sm text-red-600">
                      Passwords don't match.
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  className="btn-cta w-full py-3 font-medium rounded-md"
                >
                  Submit
                </button>
              </form>
            </>
          ) : (
            <>
              <h1 className="text-4xl font-bold text-[#171923] mb-4">
                Your password has been <br /> successfully reset!
              </h1>
              <p className="text-[#171923] text-base mb-8 leading-relaxed">
                We sent a password reset link to your email. Please check your
                inbox.
              </p>

              <button
                onClick={() => {
                  console.log("Redirecting to login");
                  // Navigate to login page or home
                  window.location.href = "/login";
                }}
                className="btn-cta w-full py-3 font-medium rounded-md"
              >
                Back to Login
              </button>
            </>
          )}
        </div>
      </div>

      {/* Right Section - Branding */}
      <div className="hidden lg:flex w-1/2 min-h-screen items-center justify-center p-8"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <img
          src="/assets/images/Logo-white-outline.png"
          alt="Join Us"
          className="w-40 h-auto object-contain"
        />
      </div>
    </div>
  );
}
